import React from 'react';
import { Facebook, Instagram, Linkedin, Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const FooterNew = () => {
  const { t, direction } = useLanguage();
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className={`grid md:grid-cols-2 lg:grid-cols-4 gap-12 ${direction === 'rtl' ? 'text-right' : ''}`}>
          {/* Company Info */}
          <div className="space-y-4">
            <img
              src="https://customer-assets.emergentagent.com/job_5584cb6f-917f-481f-bd8e-c6188ed999cc/artifacts/fyqtzyw4_Screenshot_20260214_211850_WhatsApp.jpg"
              alt="Olion Logo"
              className="h-16 w-auto mb-4"
            />
            <p className="text-gray-400 leading-relaxed">
              {t('footer.description')}
            </p>
            <div className={`flex gap-4 pt-4 ${direction === 'rtl' ? 'justify-end' : ''}`}>
              <a
                href="https://www.facebook.com/profile.php?id=61555914315148"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook-link"
                className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 flex items-center justify-center hover:scale-110 transition-transform duration-300"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/olion.detergent/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 flex items-center justify-center hover:scale-110 transition-transform duration-300"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.linkedin.com/company/olion-d%C3%A9tergents-cosm%C3%A9tiques/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin-link"
                className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 flex items-center justify-center hover:scale-110 transition-transform duration-300"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-orange-400">{t('footer.quickLinks')}</h3>
            <ul className="space-y-3">
              {['accueil', 'produits', 'apropos', 'partenaires', 'localisation', 'contact'].map((item) => (
                <li key={item}>
                  <button
                    onClick={() => scrollToSection(item)}
                    className="text-gray-400 hover:text-orange-400 transition-colors duration-300 hover:translate-x-1 inline-block"
                  >
                    {t(`nav.${item === 'accueil' ? 'home' : item === 'produits' ? 'products' : item === 'apropos' ? 'about' : item === 'partenaires' ? 'partners' : item === 'localisation' ? 'locations' : item}`)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-orange-400">{t('footer.ourProducts')}</h3>
            <ul className="space-y-3">
              <li className="text-gray-400 hover:text-orange-400 transition-colors duration-300">
                {t('categories.retail.title')}
              </li>
              <li className="text-gray-400 hover:text-orange-400 transition-colors duration-300">
                {t('categories.bulk.title')}
              </li>
              <li className="text-gray-400 hover:text-orange-400 transition-colors duration-300">
                {t('categories.industrial.title')}
              </li>
              <li className="text-gray-400 hover:text-orange-400 transition-colors duration-300">
                {t('categories.box.title')}
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-orange-400">{t('footer.contactHeader')}</h3>
            <ul className="space-y-4">
              <li className={`flex items-start gap-3 text-gray-400 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <Mail className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <span className="break-all">{t('contact.emailValue')}</span>
              </li>
              <li className={`flex items-start gap-3 text-gray-400 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <Mail className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <span className="break-all">{t('contact.emailCommercialValue')}</span>
              </li>
              <li className={`flex items-start gap-3 text-gray-400 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <Phone className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <span>{t('contact.phoneValue')}</span>
              </li>
              <li className={`flex items-start gap-3 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <MessageCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" />
                <a
                  href="https://wa.me/21695806095?text=Bonjour%20Olion%2C%20je%20viens%20de%20visiter%20votre%20site%20et%20j'aimerais%20avoir%20plus%20d'informations."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="text-gray-400 hover:text-green-400 transition-colors"
                >
                  WhatsApp · +216 95 806 095
                </a>
              </li>
              <li className={`flex items-start gap-3 text-gray-400 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <MapPin className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <span>{t('contact.addressValue')}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className={`flex flex-col md:flex-row justify-between items-center gap-4 ${direction === 'rtl' ? 'md:flex-row-reverse' : ''}`}>
            <p className="text-gray-400 text-sm">
              © {currentYear} Olion. {t('footer.rights')}
            </p>
            <div className={`flex gap-6 text-sm ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <button className="text-gray-400 hover:text-orange-400 transition-colors duration-300">
                {t('footer.privacy')}
              </button>
              <button className="text-gray-400 hover:text-orange-400 transition-colors duration-300">
                {t('footer.terms')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
